// src/components/templates/diary/DiaryTemplate.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// Import Layout
import { DiaryLayout } from '../diary/DiaryLayout';

// Import Views
import { DiaryDashboard } from '../diary/DashboardView';
import { DiaryMeetingEditor } from '../diary/MeetingEditor';
import { DiaryMeetingListView } from '../diary/MeetingListView';
import DiaryCalendarView from '../diary/CalendarView';
import { DiaryNotesView } from '../diary/NotesView';
import { DiaryContactsView } from '../diary/ContactsView';
import DiaryJournalView from '../diary/JournalView';
import { DiaryPlannerView } from '../diary/PlannerView';
import DiaryResourcesView from '../diary/ResourcesView';
import { DiarySettingsView } from '../diary/SettingsView';
import { DiaryRecordsView } from '../diary/RecordsView';
import DiaryLearningView from '../diary/LearningView';

import { AuthModal } from '../auth/AuthModal';
import { ProfileModal } from '../shared/ProfileModal';
import { ShareModal } from '../books/modals/ShareModal';
import { WorkspaceSelector } from '../diary/WorkspaceSelector';
import DiaryToolsView from '../diary/ToolsView';

import { goalService } from '../../services/goalService';
import { DiaryGoalsView } from '../diary/GoalsView';
import { getCurrentWeekNumber } from '../../constants/goalTypes';

import { learningService } from '../../services/learningService';
// Import Constants
import { ViewState, MeetingStatus } from '../diary/UIComponents';
import { diaryService } from '../../services/diaryService';
import { workspaceService } from '../../services/workspaceService';
import { toolsService } from '../../services/toolsService';
import { set } from 'react-hook-form';

// --- DEFAULT DATA ---
const DEFAULT_SETTINGS = {
  userName: 'Demo User',
  userRole: 'Workspace Admin',
  userEmail: '',
  enableNotifications: true,
  defaultMeetingDuration: 60,
  theme: 'light',
  defaultView: 'DASHBOARD',
  weekStartsOn: 0,
  timeFormat: '12h',
  enableAI: false,
  enableWorkingHours: false,
  workingHoursStart: '09:00',
  workingHoursEnd: '17:00',
  workingDays: [1, 2, 3, 4, 5]
};

// --- MAIN TEMPLATE COMPONENT ---
const DiaryTemplate = ({ 
  bookId,
  bookData = {},
  diarySettings = {},
  customStyling = {},
  aiService,
  user,
  logout
}) => {
  const navigate = useNavigate();
  
  // Check if current user is owner
  const isOwner = user?.uid === bookData?.ownerId;

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const [view, setView] = useState(ViewState.DASHBOARD);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);

  // --- DATA STATE ---
  const [meetings, setMeetings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [plannerItems, setPlannerItems] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [tools, setTools] = useState([]);
  const [records, setRecords] = useState([]);
  const [goals, setGoals] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [goalsDashboard, setGoalsDashboard] = useState(null);
  const [userSettings, setUserSettings] = useState({ ...DEFAULT_SETTINGS, ...diarySettings });

  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);

  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [createdCourses, setCreatedCourses] = useState([]);
  const [marketplaceCourses, setMarketplaceCourses] = useState([]);
  
  const [periodsByWorkspace, setPeriodsByWorkspace] = useState({ personal: [] });
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  // --- LOADING STATE ---
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- LOAD DATA ON MOUNT ---
  useEffect(() => {
    const loadData = async () => {
      if (!bookId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const [allCourses, userEnrollments] = await Promise.all([
          diaryService?.getCourses ? diaryService.getCourses(bookId) : Promise.resolve([]),
          user && diaryService?.getUserEnrollments ? diaryService.getUserEnrollments(bookId, user.uid) : Promise.resolve([])
        ]);
        
        // Load all data in parallel (except goals which needs workspaces first)
        const [
          meetingsData, 
          contactsData, 
          journalData, 
          templatesData, 
          plannerData,
          mediaData, 
          assetsData, 
          toolsData,
          workspacesData
        ] = await Promise.all([
          diaryService.getMeetings?.(bookId) || [],
          diaryService.getContacts?.(bookId) || [],
          diaryService.getJournalEntries?.(bookId) || [],
          diaryService.getMeetingTemplates?.(bookId) || [],
          diaryService.getPlannerItems?.(bookId) || [],
          diaryService.getMediaItems?.(bookId) || [],
          diaryService.getAssets?.(bookId) || [],
          diaryService.getTools?.(bookId) || [],
          workspaceService.getWorkspaces?.(bookId) || []
        ]);

        setMeetings(meetingsData);
        setContacts(contactsData);
        setJournalEntries(journalData);
        setTemplates(templatesData);
        setPlannerItems(plannerData);
        setMediaItems(mediaData);
        setAssets(assetsData);
        setTools(toolsData);
        setWorkspaces(workspacesData);
        setCourses(allCourses || []);
        setEnrollments(userEnrollments || []);

        // Load goals data AFTER workspaces are set
        await loadGoalsDataWithWorkspaces(workspacesData);

        setError(null);
      } catch (err) {
        console.error('Error loading diary data:', err);
        setError('Failed to load data. Please refresh.');
      } finally {
        setIsLoading(false);
      }
    };

    // Goals loader that accepts workspaces directly
    const loadGoalsDataWithWorkspaces = async (workspacesList) => {
      try {
        const periods = await goalService.getPeriodsByWorkspace(bookId, workspacesList);
        setPeriodsByWorkspace(periods || { personal: [] });
        
        const dashboard = await goalService.getMultiWorkspaceDashboard(bookId, workspacesList);
        setGoalsDashboard(dashboard);
        
        // Find first active period
        let activeFound = null;
        
        const personalActive = periods?.personal?.find(p => p.status === 'active');
        if (personalActive) {
          activeFound = { period: personalActive, workspaceId: null };
        }
        
        if (!activeFound && workspacesList.length > 0) {
          for (const ws of workspacesList) {
            const wsActive = periods?.[ws.id]?.find(p => p.status === 'active');
            if (wsActive) {
              activeFound = { period: wsActive, workspaceId: ws.id };
              break;
            }
          }
        }
        
        if (activeFound) {
          setSelectedWorkspaceId(activeFound.workspaceId);
          setSelectedPeriodId(activeFound.period.id);
          setActivePeriod(activeFound.period);
          
          const periodGoals = await goalService.getGoals(bookId, { periodId: activeFound.period.id });
          setGoals(periodGoals || []);
        }
      } catch (err) {
        console.error('Error loading goals data:', err);
      }
    };

    loadData();
  }, [bookId, user]);

  const userWorkspaces = workspaces.filter(ws => 
      ws.ownerId === user?.uid || 
      ws.memberIds?.includes(user?.uid) ||
      ws.type === 'PERSONAL'
    );

  // --- LOAD WORKSPACE MEMBERS ---
  useEffect(() => {
    const loadWorkspaceMembers = async () => {
      if (!currentWorkspace?.id || !bookId) {
        setWorkspaceMembers([]);
        return;
      }
      
      try {
        const members = await workspaceService.getWorkspaceMembers?.(bookId, currentWorkspace.id) || [];
        setWorkspaceMembers(members);
      } catch (err) {
        console.error('Error loading workspace members:', err);
        setWorkspaceMembers([]);
      }
    };

    loadWorkspaceMembers();
  }, [bookId, currentWorkspace?.id]);

  // --- COMPUTED STATS ---
  const stats = useMemo(() => ({
    contacts: contacts.length,
    meetings: meetings.length,
    plannedMeetings: meetings.filter(m => m.status === MeetingStatus.PLANNED).length,
    activeMeetings: meetings.filter(m => m.status === MeetingStatus.ACTIVE).length,
    notes: meetings.reduce((sum, m) => sum + (m.meetingNotes?.length || 0), 0),
    journalEntries: journalEntries.length,
    plannerItems: plannerItems.length,
    resources: mediaItems.length + assets.length + tools.length,
    courses: enrollments?.length || 0,
    // Goals stats
    activeGoals: goals.filter(g => g.status === 'active').length,
    goalsOnTrack: goalsDashboard?.goalsOnTrack || 0,
    currentWeek: activePeriod ? getCurrentWeekNumber(activePeriod.startDate) : null,
    // Notifications
    notifications: meetings.filter(m => {
      if (m.status !== MeetingStatus.PLANNED) return false;
      const meetingDate = new Date(m.date);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return meetingDate <= tomorrow;
    }).length
  }), 
    [meetings, 
    contacts, 
    journalEntries, 
    plannerItems, 
    mediaItems, 
    assets, 
    tools, 
    goals, 
    goalsDashboard, 
    activePeriod, 
    enrolledCourses 
  ]);
  
  // --- MEETING HANDLERS ---
  const handleCreateMeeting = (initialDate, initialTime) => {
    const newMeeting = {
      id: Math.random().toString(36).substr(2, 9),
      title: 'New Meeting',
      date: initialDate || new Date().toISOString().split('T')[0],
      time: initialTime || '09:00',
      durationMinutes: userSettings.defaultMeetingDuration,
      location: '',
      type: 'VIRTUAL',
      meetingLink: '',
      status: MeetingStatus.PLANNED,
      recurrence: 'NONE',
      participants: [],
      agenda: [],
      minutes: [],
      objectives: [],
      meetingNotes: [],
      createdAt: new Date().toISOString()
    };
    
    setMeetings(prev => [newMeeting, ...prev]);
    setSelectedMeetingId(newMeeting.id);
    setView(ViewState.MEETING_DETAIL);
  };

  const handleSelectMeeting = (meeting) => {
    setSelectedMeetingId(meeting.id);
    setView(ViewState.MEETING_DETAIL);
  };

  const handleSaveMeeting = async (updatedMeeting) => {
    setMeetings(prev => prev.map(m => m.id === updatedMeeting.id ? updatedMeeting : m));
    
    if (diaryService?.saveMeeting) {
      try {
        await diaryService.saveMeeting(bookId, updatedMeeting);
      } catch (err) {
        console.error('Error saving meeting:', err);
      }
    }
  };

  const handleDeleteMeeting = async (id) => {
    setMeetings(prev => prev.filter(m => m.id !== id));
    
    if (selectedMeetingId === id) {
      setSelectedMeetingId(null);
      setView(ViewState.MEETINGS);
    }

    if (diaryService?.deleteMeeting) {
      try {
        await diaryService.deleteMeeting(bookId, id);
      } catch (err) {
        console.error('Error deleting meeting:', err);
      }
    }
  };

  const handleDuplicateMeeting = (meeting) => {
    const duplicate = {
      ...meeting,
      id: Math.random().toString(36).substr(2, 9),
      title: `${meeting.title} (Copy)`,
      status: MeetingStatus.PLANNED,
      date: new Date().toISOString().split('T')[0],
      minutes: [],
      createdAt: new Date().toISOString()
    };
    setMeetings(prev => [duplicate, ...prev]);
  };

  const handleSaveTemplate = async (template) => {
    const newTemplate = {
      ...template,
      id: template.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    
    setTemplates(prev => [...prev.filter(t => t.id !== newTemplate.id), newTemplate]);

    if (diaryService?.saveMeetingTemplate) {
      try {
        await diaryService.saveMeetingTemplate(bookId, newTemplate);
      } catch (err) {
        console.error('Error saving template:', err);
      }
    }
  };

  // --- CONTACT HANDLERS ---
  const handleAddContact = async (contact) => {
    const newContact = {
      ...contact,
      id: contact.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    
    setContacts(prev => [newContact, ...prev]);

  
      try {
        await diaryService.saveContact(bookId, newContact);
      } catch (err) {
        console.error('Error saving contact:', err);
      
    }
  };

  const handleUpdateContact = async (contact) => {
    setContacts(prev => prev.map(c => c.id === contact.id ? contact : c));

    if (diaryService?.saveContact) {
      try {
        await diaryService.saveContact(bookId, contact);
      } catch (err) {
        console.error('Error updating contact:', err);
      }
    }
  };

  const handleDeleteContact = async (id) => {
    setContacts(prev => prev.filter(c => c.id !== id));

    if (diaryService?.deleteContact) {
      try {
        await diaryService.deleteContact(bookId, id);
      } catch (err) {
        console.error('Error deleting contact:', err);
      }
    }
  };

  // --- JOURNAL HANDLERS ---
  const handleAddJournalEntry = async (entry) => {
    const newEntry = {
      ...entry,
      id: entry.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    
    setJournalEntries(prev => [newEntry, ...prev]);

    if (diaryService?.saveJournalEntry) {
      try {
        await diaryService.saveJournalEntry(bookId, newEntry);
      } catch (err) {
        console.error('Error saving journal entry:', err);
      }
    }
  };

  const handleUpdateJournalEntry = async (entry) => {
    setJournalEntries(prev => prev.map(e => e.id === entry.id ? entry : e));

    if (diaryService?.saveJournalEntry) {
      try {
        await diaryService.saveJournalEntry(bookId, entry);
      } catch (err) {
        console.error('Error updating journal entry:', err);
      }
    }
  };

  const handleDeleteJournalEntry = async (id) => {
    setJournalEntries(prev => prev.filter(e => e.id !== id));

    if (diaryService?.deleteJournalEntry) {
      try {
        await diaryService.deleteJournalEntry(bookId, id);
      } catch (err) {
        console.error('Error deleting journal entry:', err);
      }
    }
  };

  const handlePinJournalEntry = async (entry) => {
    await handleUpdateJournalEntry(entry);
  };

  const handleAddPlannerItem = async (item) => {
    const newItem = {
      ...item,
      id: item.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    
    setPlannerItems(prev => [...prev, newItem]);

    if (diaryService?.savePlannerItem) {
      try {
        await diaryService.savePlannerItem(bookId, newItem);
      } catch (err) {
        console.error('Error saving planner item:', err);
      }
    }
  };

  const handleCalendarQuickAdd = (type, date) => {
    const safeDate = date || new Date().toISOString().split("T")[0];

    const newItem = {
      type,
      date: safeDate,
      title: "New Planner Item",
      status: "TODO",
      startTime: "",
      endTime: "",
      reminders: []
    };

    handleAddPlannerItem(newItem);
  };


  const handleUpdatePlannerItem = async (item) => {
    setPlannerItems(prev => prev.map(i => i.id === item.id ? item : i));

    if (diaryService?.savePlannerItem) {
      try {
        await diaryService.savePlannerItem(bookId, item);
      } catch (err) {
        console.error('Error updating planner item:', err);
      }
    }
  };

  const handleDeletePlannerItem = async (id) => {
    setPlannerItems(prev => prev.filter(i => i.id !== id));

    if (diaryService?.deletePlannerItem) {
      try {
        await diaryService.deletePlannerItem(bookId, id);
      } catch (err) {
        console.error('Error deleting planner item:', err);
      }
    }
  };

  // --- MEDIA HANDLERS ---
  const handleAddMedia = async (item) => {
    const newItem = {
      ...item,
      id: item.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    setMediaItems(prev => [...prev, newItem]);
    if (diaryService?.saveMediaItem) {
      try { await diaryService.saveMediaItem(bookId, newItem); } 
      catch (err) { console.error('Error saving media:', err); }
    }
  };

  const handleUpdateMedia = async (item) => {
    setMediaItems(prev => prev.map(i => i.id === item.id ? item : i));
    if (diaryService?.saveMediaItem) {
      try { await diaryService.saveMediaItem(bookId, item); } 
      catch (err) { console.error('Error updating media:', err); }
    }
  };

  const handleDeleteMedia = async (id) => {
    setMediaItems(prev => prev.filter(i => i.id !== id));
    if (diaryService?.deleteMediaItem) {
      try { await diaryService.deleteMediaItem(bookId, id); } 
      catch (err) { console.error('Error deleting media:', err); }
    }
  };

  // --- ASSET HANDLERS ---
  const handleAddAsset = async (item) => {
    const newItem = {
      ...item,
      id: item.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    setAssets(prev => [...prev, newItem]);
    if (diaryService?.saveAsset) {
      try { await diaryService.saveAsset(bookId, newItem); } 
      catch (err) { console.error('Error saving asset:', err); }
    }
  };

  const handleUpdateAsset = async (item) => {
    setAssets(prev => prev.map(i => i.id === item.id ? item : i));
    if (diaryService?.saveAsset) {
      try { await diaryService.saveAsset(bookId, item); } 
      catch (err) { console.error('Error updating asset:', err); }
    }
  };

  const handleDeleteAsset = async (id) => {
    setAssets(prev => prev.filter(i => i.id !== id));
    if (diaryService?.deleteAsset) {
      try { await diaryService.deleteAsset(bookId, id); } 
      catch (err) { console.error('Error deleting asset:', err); }
    }
  };

  // --- TOOL HANDLERS ---
  const handleAddTool = async (item) => {
    const newItem = {
      ...item,
      id: item.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString()
    };
    setTools(prev => [...prev, newItem]);
    if (diaryService?.saveTool) {
      try { await diaryService.saveTool(bookId, newItem); } 
      catch (err) { console.error('Error saving tool:', err); }
    }
  };

  const handleAddBooksAsTools = async (selectedBooks) => {
    try {
      for (const book of selectedBooks) {
        // Construct correct URL based on custom domain settings
        const bookUrl = book.customDomain 
          ? `https://${book.customDomain}${book.customPath ? '/' + book.customPath : ''}`
          : `${window.location.origin}/book/${book.id}`;
        
        const tool = {
          type: 'INTERNAL',
          sourceType: 'book',
          sourceBookId: book.id,
          name: book.botName || book.title || 'Untitled Book',
          description: book.description || `Published ${book.templateType || 'book'}`,
          url: bookUrl,
          embedCode: `<iframe src="${bookUrl}" width="100%" height="600" frameborder="0"></iframe>`,
          category: book.templateType || 'book',
          tags: [book.templateType || 'book', 'published'],
          icon: 'book',
          metadata: {
            templateType: book.templateType,
            customDomain: book.customDomain,
            customPath: book.customPath,
            published: book.published,
            addedAt: new Date().toISOString()
          }
        };
        
        await diaryService.saveTool(bookId, tool);
      }
      
      // Reload tools
      const updated = await diaryService.getTools(bookId);
      setTools(updated);
    } catch (error) {
      console.error('Error adding books as tools:', error);
      alert('Failed to add books. Please try again.');
    }
  };

  const handleUpdateTool = async (item) => {
    setTools(prev => prev.map(i => i.id === item.id ? item : i));
    if (diaryService?.saveTool) {
      try { await diaryService.saveTool(bookId, item); } 
      catch (err) { console.error('Error updating tool:', err); }
    }
  };

  const handleDeleteTool = async (id) => {
    setTools(prev => prev.filter(i => i.id !== id));
    if (diaryService?.deleteTool) {
      try { await diaryService.deleteTool(bookId, id); } 
      catch (err) { console.error('Error deleting tool:', err); }
    }
  };

  // --- RECORDS HANDLERS ---
  const handleAddRecord = async (record) => {
    try {
      const recordId = await diaryService.saveRecord(bookId, record);
      const updated = await diaryService.getRecords(bookId);
      setRecords(updated);
    } catch (error) {
      console.error('Error adding record:', error);
      alert('Failed to add record. Please try again.');
    }
  };

  const handleUpdateRecord = async (record) => {
    try {
      await diaryService.saveRecord(bookId, record);
      const updated = await diaryService.getRecords(bookId);
      setRecords(updated);
    } catch (error) {
      console.error('Error updating record:', error);
      alert('Failed to update record. Please try again.');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    try {
      await diaryService.deleteRecord(bookId, recordId);
      setRecords(prev => prev.filter(r => r.id !== recordId));
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Failed to delete record. Please try again.');
    }
  };

  const handlePinRecord = async (record) => {
    try {
      const updated = { ...record, isPinned: !record.isPinned };
      await diaryService.saveRecord(bookId, updated);
      setRecords(prev => prev.map(r => r.id === record.id ? updated : r));
    } catch (error) {
      console.error('Error pinning record:', error);
      alert('Failed to update record. Please try again.');
    }
  };

  // --- REFRESH GOALS (for use after mutations) ---
  const refreshGoals = async () => {
    try {
      const periods = await goalService.getPeriodsByWorkspace(bookId, workspaces);
      setPeriodsByWorkspace(periods || { personal: [] });
      
      const dashboard = await goalService.getMultiWorkspaceDashboard(bookId, workspaces);
      setGoalsDashboard(dashboard);
      
      // Reload current period's goals
      if (selectedPeriodId) {
        const period = await goalService.getPeriod(bookId, selectedPeriodId);
        setActivePeriod(period);
        
        if (period) {
          const periodGoals = await goalService.getGoals(bookId, { periodId: period.id });
          setGoals(periodGoals || []);
        }
      }
    } catch (err) {
      console.error('Error refreshing goals:', err);
    }
  };

  const handleCreatePeriod = async (periodData) => {
    try {
      const newPeriod = await goalService.createFuturePeriod(bookId, periodData);
      
      // Optimistic update
      setPeriodsByWorkspace(prev => {
        const key = periodData.workspaceId || 'personal';
        return {
          ...prev,
          [key]: [...(prev[key] || []), newPeriod]
        };
      });
      
      // Set as selected if it's active
      if (newPeriod.status === 'active') {
        setSelectedPeriodId(newPeriod.id);
        setSelectedWorkspaceId(periodData.workspaceId || null);
        setActivePeriod(newPeriod);
      }
      
      await refreshGoals();
      return newPeriod;
    } catch (err) {
      console.error('Error creating period:', err);
      throw err;
    }
  };

  const handleUpdatePeriod = async (periodId, updates) => {
    try {
      await goalService.updatePeriod(bookId, periodId, updates);
      
      // Optimistic update
      setActivePeriod(prev => prev?.id === periodId ? { ...prev, ...updates } : prev);
      setPeriodsByWorkspace(prev => {
        const updated = {};
        for (const [key, periods] of Object.entries(prev)) {
          updated[key] = periods.map(p => p.id === periodId ? { ...p, ...updates } : p);
        }
        return updated;
      });
      
      await refreshGoals();
    } catch (err) {
      console.error('Error updating period:', err);
      throw err;
    }
  };

  const handleCompletePeriod = async (periodId) => {
    try {
      await goalService.completePeriod(bookId, periodId);
      
      // Optimistic update
      setActivePeriod(prev => prev?.id === periodId ? { ...prev, status: 'completed' } : prev);
      setPeriodsByWorkspace(prev => {
        const updated = {};
        for (const [key, periods] of Object.entries(prev)) {
          updated[key] = periods.map(p => p.id === periodId ? { ...p, status: 'completed' } : p);
        }
        return updated;
      });
      
      // Clear selection if this was selected
      if (selectedPeriodId === periodId) {
        setSelectedPeriodId(null);
        setActivePeriod(null);
        setGoals([]);
      }
      
      await refreshGoals();
    } catch (err) {
      console.error('Error completing period:', err);
      throw err;
    }
  };

  const handleActivatePeriod = async (periodId) => {
    try {
      await goalService.activatePeriod(bookId, periodId);
      
      // Optimistic update
      setActivePeriod(prev => prev?.id === periodId ? { ...prev, status: 'active' } : prev);
      setPeriodsByWorkspace(prev => {
        const updated = {};
        for (const [key, periods] of Object.entries(prev)) {
          updated[key] = periods.map(p => p.id === periodId ? { ...p, status: 'active' } : p);
        }
        return updated;
      });
      
      // Also activate draft goals
      setGoals(prev => prev.map(g => 
        g.status === 'draft' ? { ...g, status: 'active' } : g
      ));
      
      await refreshGoals();
    } catch (err) {
      console.error('Error activating period:', err);
      throw err;
    }
  };

  const handleCreateGoal = async (goalData) => {
    try {
      const newGoal = await goalService.createGoal(bookId, goalData, selectedPeriodId);
      
      // Optimistic update
      setGoals(prev => [...prev, newGoal]);
      
      // Update period's goalIds
      setActivePeriod(prev => prev ? {
        ...prev,
        goalIds: [...(prev.goalIds || []), newGoal.id]
      } : prev);
      
      await refreshGoals();
      return newGoal;
    } catch (err) {
      console.error('Error creating goal:', err);
      throw err;
    }
  };

  const handleUpdateGoal = async (goalId, updates) => {
    try {
      await goalService.updateGoal(bookId, goalId, updates);
      
      // Optimistic update
      setGoals(prev => prev.map(g => 
        g.id === goalId ? { ...g, ...updates } : g
      ));
      
      await refreshGoals();
    } catch (err) {
      console.error('Error updating goal:', err);
      throw err;
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await goalService.deleteGoal(bookId, goalId);
      
      // Optimistic update
      setGoals(prev => prev.filter(g => g.id !== goalId));
      
      // Update period's goalIds
      setActivePeriod(prev => prev ? {
        ...prev,
        goalIds: (prev.goalIds || []).filter(id => id !== goalId)
      } : prev);
      
      await refreshGoals();
    } catch (err) {
      console.error('Error deleting goal:', err);
      throw err;
    }
  };

  const handleCompleteGoal = async (goalId, completionNotes = '') => {
    try {
      await goalService.completeGoal(bookId, goalId, completionNotes);
      
      // Optimistic update
      setGoals(prev => prev.map(g => 
        g.id === goalId ? { ...g, status: 'completed', completionNotes } : g
      ));
      
      await refreshGoals();
    } catch (err) {
      console.error('Error completing goal:', err);
      throw err;
    }
  };

  const handleSaveWeeklyScore = async (goalId, weekNumber, scoreData) => {
    try {
      const score = await goalService.saveWeeklyScore(bookId, goalId, weekNumber, scoreData);
      
      // Optimistic update - update goal's weekly scores
      setGoals(prev => prev.map(g => {
        if (g.id !== goalId) return g;
        
        const existingScores = g.weeklyScores || [];
        const scoreIndex = existingScores.findIndex(s => s.weekNumber === weekNumber);
        
        let updatedScores;
        if (scoreIndex >= 0) {
          updatedScores = existingScores.map((s, i) => 
            i === scoreIndex ? { weekNumber, score: score.overallScore, scoredAt: new Date().toISOString() } : s
          );
        } else {
          updatedScores = [...existingScores, { weekNumber, score: score.overallScore, scoredAt: new Date().toISOString() }];
        }
        
        return { ...g, weeklyScores: updatedScores };
      }));
      
      await refreshGoals();
      return score;
    } catch (err) {
      console.error('Error saving weekly score:', err);
      throw err;
    }
  };

  const handleUpdateLagMeasure = async (goalId, currentValue) => {
    try {
      await goalService.updateLagMeasure(bookId, goalId, currentValue);
      
      // Optimistic update
      setGoals(prev => prev.map(g => 
        g.id === goalId ? { 
          ...g, 
          lagMeasure: { ...g.lagMeasure, currentValue } 
        } : g
      ));
      
      await refreshGoals();
    } catch (err) {
      console.error('Error updating lag measure:', err);
      throw err;
    }
  };

  const handleAddLeadMeasure = async (goalId, measureData) => {
    try {
      const measure = await goalService.addLeadMeasure(bookId, goalId, measureData);
      
      // Optimistic update
      setGoals(prev => prev.map(g => 
        g.id === goalId ? { 
          ...g, 
          leadMeasures: [...(g.leadMeasures || []), measure] 
        } : g
      ));
      
      await refreshGoals();
      return measure;
    } catch (err) {
      console.error('Error adding lead measure:', err);
      throw err;
    }
  };

  const handleRecordLeadProgress = async (goalId, measureId, weekNumber, actualValue) => {
    try {
      await goalService.recordLeadMeasureProgress(bookId, goalId, measureId, weekNumber, actualValue);
      
      // Optimistic update
      setGoals(prev => prev.map(g => {
        if (g.id !== goalId) return g;
        
        const updatedMeasures = (g.leadMeasures || []).map(m => {
          if (m.id !== measureId) return m;
          return {
            ...m,
            weeklyActuals: {
              ...m.weeklyActuals,
              [`week${weekNumber}`]: actualValue
            }
          };
        });
        
        return { ...g, leadMeasures: updatedMeasures };
      }));
      
      await refreshGoals();
    } catch (err) {
      console.error('Error recording lead progress:', err);
      throw err;
    }
  };

  // COURSE HANDLERS
  const handleAddCourse = async (course) => {
    const newCourse = {
      ...course,
      id: course.id || Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString(),
      isPublished: false
    };
    
    setCourses(prev => [newCourse, ...prev]);

    if (diaryService?.saveCourse) {
      try {
        await diaryService.saveCourse(bookId, newCourse);
      } catch (err) {
        console.error('Error saving course:', err);
      }
    }
  };

  const handleUpdateCourse = async (course) => {
    setCourses(prev => prev.map(c => c.id === course.id ? course : c));

    if (diaryService?.saveCourse) {
      try {
        await diaryService.saveCourse(bookId, course);
      } catch (err) {
        console.error('Error updating course:', err);
      }
    }
  };

  const handleDeleteCourse = async (id) => {
    setCourses(prev => prev.filter(c => c.id !== id));

    if (diaryService?.deleteCourse) {
      try {
        await diaryService.deleteCourse(bookId, id);
      } catch (err) {
        console.error('Error deleting course:', err);
      }
    }
  };

  // ENROLLMENT HANDLERS
  const handleEnrollCourse = async (courseId, paymentInfo = null) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const newEnrollment = {
      id: Math.random().toString(36).substr(2, 9),
      courseId,
      userId: user.uid,
      userEmail: user.email,
      enrolledAt: new Date().toISOString(),
      progress: 0,
      completedLessons: [],
      completedAssessments: {},
      competencyScores: {},
      totalTimeSpent: 0,
      status: 'active',
      paymentInfo: paymentInfo || null
    };
    
    setEnrollments(prev => [newEnrollment, ...prev]);

    if (diaryService?.saveEnrollment) {
      try {
        await diaryService.saveEnrollment(bookId, newEnrollment);
        
        // Reload enrollments with course data
        const userEnrollments = await diaryService.getUserEnrollments(bookId, user.uid);
        setEnrollments(userEnrollments);
      } catch (err) {
        console.error('Error enrolling in course:', err);
      }
    }
  };

  const handleUpdateEnrollment = async (enrollment) => {
    setEnrollments(prev => prev.map(e => e.id === enrollment.id ? enrollment : e));

    if (diaryService?.saveEnrollment) {
      try {
        await diaryService.saveEnrollment(bookId, enrollment);
      } catch (err) {
        console.error('Error updating enrollment:', err);
      }
    }
  };

  const handleUnenrollCourse = async (enrollmentId) => {
    setEnrollments(prev => prev.filter(e => e.id !== enrollmentId));

    if (diaryService?.deleteEnrollment) {
      try {
        await diaryService.deleteEnrollment(bookId, enrollmentId);
      } catch (err) {
        console.error('Error unenrolling from course:', err);
      }
    }
  };

  // LESSON HANDLERS (if needed at template level)
  const handleSaveLesson = async (courseId, lesson) => {
    if (diaryService?.saveLesson) {
      try {
        await diaryService.saveLesson(bookId, courseId, lesson);
        
        // Reload courses to update lesson count
        const allCourses = await diaryService.getCourses(bookId);
        setCourses(allCourses);
      } catch (err) {
        console.error('Error saving lesson:', err);
      }
    }
  };

  // --- SETTINGS HANDLERS ---
  const handleUpdateSettings = async (newSettings) => {
    setUserSettings(newSettings);

    if (diaryService?.saveSettings) {
      try {
        await diaryService.saveSettings(bookId, newSettings);
      } catch (err) {
        console.error('Error saving settings:', err);
      }
    }
  };

  const handleResetData = async () => {
    setMeetings([]);
    setContacts([]);
    setJournalEntries([]);
    setTemplates([]);
    
    if (diaryService?.resetData) {
      try {
        await diaryService.resetData(bookId);
      } catch (err) {
        console.error('Error resetting data:', err);
      }
    }
  };

  const handleExportData = () => {
    const data = {
      meetings,
      contacts,
      journalEntries,
      plannerItems,
      templates,
      settings: userSettings,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lifelog-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- NAVIGATION ---
  const handleNavigate = (newView) => {
    if (newView !== ViewState.MEETING_DETAIL) {
      setSelectedMeetingId(null);
    }
    setView(newView);
  };

  const handleBackFromMeeting = () => {
    setSelectedMeetingId(null);
    setView(ViewState.MEETINGS);
  };

  

  // --- GET CURRENT MEETING ---
  const currentMeeting = meetings.find(m => m.id === selectedMeetingId);

  // --- LOADING STATE ---
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your diary...</p>
        </div>
      </div>
    );
  }

  // --- ERROR STATE ---
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <p className="text-gray-800 font-semibold mb-2">Something went wrong</p>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // --- RENDER ---
  return (
    <>
    <DiaryLayout 
      bookData={bookData}
      currentView={view} 
      onNavigate={handleNavigate} 
      userSettings={userSettings}
      stats={stats}

      customStyling={customStyling}
      user={user}
      isOwner={isOwner}
      logout={logout}
      navigate={navigate}
      // Modal setters
      setShowAuthModal={setShowAuthModal}
      setShowProfileModal={setShowProfileModal}
      setShowShareModal={setShowShareModal}
    >
      {/* Dashboard */}
      {view === ViewState.DASHBOARD && (
        <div className="h-full overflow-y-auto">
          <DiaryDashboard 
            bookId={bookId}
            customStyling={customStyling}
            meetings={meetings} 
            contacts={contacts}
            journalEntries={journalEntries}
            onCreateMeeting={() => handleCreateMeeting()}
            onSelectMeeting={handleSelectMeeting}
            onNavigate={handleNavigate}
          />
        </div>
      )}

      {/* Goals - 12-Week Year System */}
      {view === ViewState.GOALS && (
        <DiaryGoalsView 
          bookId={bookId}
          customStyling={customStyling}
          // Data
          goals={goals}
          activePeriod={activePeriod}
          dashboard={goalsDashboard}
          // Workspaces
          workspaces={userWorkspaces}
          workspaceMembers={workspaceMembers}
          currentWorkspace={currentWorkspace}
          // Period handlers
          onCreatePeriod={handleCreatePeriod}
          onUpdatePeriod={handleUpdatePeriod}
          onCompletePeriod={handleCompletePeriod}
          // Goal handlers
          onCreateGoal={handleCreateGoal}
          onUpdateGoal={handleUpdateGoal}
          onDeleteGoal={handleDeleteGoal}
          onCompleteGoal={handleCompleteGoal}
          // Score handlers
          onSaveWeeklyScore={handleSaveWeeklyScore}
          onUpdateLagMeasure={handleUpdateLagMeasure}
          // Lead measure handlers
          onAddLeadMeasure={handleAddLeadMeasure}
          onRecordLeadProgress={handleRecordLeadProgress}
          // Service for analytics
          goalService={goalService}
        />
      )}
      
      {/* Meeting List */}
      {view === ViewState.MEETINGS && (
        <DiaryMeetingListView 
          bookId={bookId}
          customStyling={customStyling}
          meetings={meetings}
          onCreateMeeting={() => handleCreateMeeting()}
          onSelectMeeting={handleSelectMeeting}
          onDeleteMeeting={handleDeleteMeeting}
          onDuplicateMeeting={handleDuplicateMeeting}
        />
      )}

      {/* Meeting Detail/Editor */}
      {view === ViewState.MEETING_DETAIL && currentMeeting && (
        <DiaryMeetingEditor 
          bookId={bookId}
          user={user}
          customStyling={customStyling}
          meeting={currentMeeting} 
          workspaces={userWorkspaces}
          onWorkspaceChange={(ws) => setCurrentWorkspace(ws)}
          onSave={handleSaveMeeting}
          onBack={handleBackFromMeeting}
          templates={templates}
          onSaveTemplate={handleSaveTemplate}
          aiService={aiService}
        />
      )}

      {/* Calendar */}
        {view === ViewState.CALENDAR && (
          <DiaryCalendarView 
            bookId={bookId}
            customStyling={customStyling}
            meetings={meetings}
            plannerItems={plannerItems}
            onSelectMeeting={handleSelectMeeting}
            onCreateMeeting={handleCreateMeeting}
            onNavigate={setView}
            onCreatePlannerItem={handleCalendarQuickAdd}
          />
        )}

      {/* Contacts */}
      {view === ViewState.CONTACTS && (
        <DiaryContactsView 
          bookId={bookId}
          customStyling={customStyling}
          contacts={contacts}
          onAddContact={handleAddContact}
          onDeleteContact={handleDeleteContact}
          onUpdateContact={handleUpdateContact}
          diaryService={diaryService}
        />
      )}

      {/* Journal */}
      {view === ViewState.JOURNAL && (
        <DiaryJournalView 
          bookId={bookId}
          customStyling={customStyling}
          entries={journalEntries}
          onAddEntry={handleAddJournalEntry}
          onUpdateEntry={handleUpdateJournalEntry}
          onDeleteEntry={handleDeleteJournalEntry}
          onPinEntry={handlePinJournalEntry}
        />
      )}

      {/* Records */}
      {view === ViewState.RECORDS && (
        <DiaryRecordsView 
          bookId={bookId}
          customStyling={customStyling}
          records={records}
          resources={assets}
          onAddRecord={handleAddRecord}
          onUpdateRecord={handleUpdateRecord}
          onDeleteRecord={handleDeleteRecord}
          onPinRecord={handlePinRecord}
        />
      )}
      
      {/* Notes (from all meetings) */}
      {view === ViewState.NOTES && (
        <DiaryNotesView 
          bookId={bookId}
          customStyling={customStyling}
          meetings={meetings}
          onSelectMeeting={handleSelectMeeting}
        />
      )}

      {/* Planner - Tasks, Events, Bookings */}
      {view === ViewState.PLANNER && (
        <DiaryPlannerView 
          bookId={bookId}
          customStyling={customStyling}
          items={plannerItems}
          onAddItem={handleAddPlannerItem}
          onUpdateItem={handleUpdatePlannerItem}
          onDeleteItem={handleDeletePlannerItem}
          diaryService={diaryService}
        />
      )}

      {view === ViewState.RESOURCES && (
        <DiaryResourcesView 
          bookId={bookId}
          customStyling={customStyling}
          media={mediaItems}
          assets={assets}
          tools={tools}
          onAddMedia={handleAddMedia}
          onUpdateMedia={handleUpdateMedia}
          onDeleteMedia={handleDeleteMedia}
          onAddAsset={handleAddAsset}
          onUpdateAsset={handleUpdateAsset}
          onDeleteAsset={handleDeleteAsset}
          onAddTool={handleAddTool}
          onUpdateTool={handleUpdateTool}
          onDeleteTool={handleDeleteTool}
          onAddBooksAsTools={handleAddBooksAsTools}
          diaryService={diaryService}
        />
      )}

      {/* Learning - Courses, Marketplace */}
      {view === ViewState.LEARNING && (
        <DiaryLearningView
          bookId={bookId}
          customStyling={customStyling}
          user={user}
          isOwner={isOwner}
          // Data props
          courses={courses}
          enrollments={enrollments}
          // Handler props
          onAddCourse={handleAddCourse}
          onUpdateCourse={handleUpdateCourse}
          onDeleteCourse={handleDeleteCourse}
          onEnrollCourse={handleEnrollCourse}
          onUpdateEnrollment={handleUpdateEnrollment}
          onUnenrollCourse={handleUnenrollCourse}
          onSaveLesson={handleSaveLesson}
          // Service access
          diaryService={diaryService}
        />
      )}

      {/* Tools - Standalone View */}
      {view === ViewState.TOOLS && (
        <DiaryToolsView 
          bookId={bookId}
          customStyling={customStyling}
          tools={tools}
          isOwner={isOwner}
          workspaces={userWorkspaces}
          onAddTool={handleAddTool}
          onUpdateTool={handleUpdateTool}
          onDeleteTool={handleDeleteTool}
          toolsService={toolsService}
        />
      )}

      {/* Settings */}
      {view === ViewState.SETTINGS && (
        <DiarySettingsView 
          bookId={bookId}
          bookData={bookData}
          user={user}
          contacts={contacts}
          customStyling={customStyling}
          settings={userSettings}
          onSaveSettings={handleUpdateSettings}
          onResetData={handleResetData}
          onExportData={handleExportData}
        />
      )}
    </DiaryLayout>

    {/* Auth Modal */}
    <AuthModal 
      isOpen={showAuthModal} 
      onClose={() => setShowAuthModal(false)} 
    />

    {/* Profile Modal */}
    {showProfileModal && (
      <ProfileModal 
        onClose={() => setShowProfileModal(false)}
        onSave={() => setShowProfileModal(false)}
      />
    )}

    {/* Share Modal */}
    <ShareModal 
      content={bookData}
      isOpen={showShareModal} 
      onClose={() => setShowShareModal(false)} 
    />
  </>
  );
};

export default DiaryTemplate;